import React from 'react'
import { useToggle } from 'react-use'
import { useError, useToast } from '@ztechapps/common.hook'
import { getWindow } from '@ztechapps/common.utils'
import { BusyButton } from '@ztechapps/webui.button'
import { buildBlogPreviewUrl } from '@ztechapps/blog.common'
import { useBlogPreviewSlug } from '../hooks/useBlogPreviewSlug'

export type OpenPreviewButtonProps = {
  blogId: string
  slug: string
  disabled?: boolean
  onSlugSaved?: () => Promise<void> | void
}

/**
 * Opens the standalone preview for a blog post in a new tab, minting and
 * saving a slug first when the post has none — the opened tab then shows the
 * post it was opened from.
 */
export const OpenPreviewButton = ({ blogId, slug, disabled = false, onSlugSaved }: OpenPreviewButtonProps) => {
  const { getPreviewSlug } = useBlogPreviewSlug({ blogId, slug, onSlugSaved })
  const { showSuccess } = useToast()
  const { onError } = useError()
  const [opening, setOpening] = useToggle(false)

  const onOpen = async () => {
    setOpening(true)
    try {
      const resolvedSlug = await getPreviewSlug()
      // New-tab shape follows the rank-1 open-in-new-tab precedent
      // (`apps/panelpunches/admin/src/view/productions/item/consumption/RowAction.tsx:107-114`),
      // routed through `getWindow()` per the WebUI guide's no-bare-window rule.
      getWindow().open(buildBlogPreviewUrl(getWindow().location.origin, resolvedSlug), '_blank', 'noopener,noreferrer')
      showSuccess({ message: 'Preview opened in a new tab.' })
    } catch (e) {
      onError(e)
    } finally {
      setOpening(false)
    }
  }

  return (
    <BusyButton
      text="Open preview"
      icon="arrow-up-right-from-square"
      isBusy={opening}
      disabled={disabled || !blogId}
      onClick={onOpen}
    />
  )
}
